import {Card, showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {useCallback} from 'react';

import {isNil, mergeAll} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {CorrectionType} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {
  DmsDataGrid,
  WAREHOUSE_GRID_CODES,
  catchUnhandledDataGridActions,
  logError,
  refreshDmsDatagrid,
} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId, useLatestValue} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

const PREDEFINED_COLUMNS = ['articleId', 'warehouseId'] as const;

interface ReceiveNoteCorrectionAddMaterialListProps extends RequiredTestIdProps {
  receiveNoteId: string;
  onAddMaterialToBasket: (itemsIds: string[], correctionType: CorrectionType) => Promise<void>;
  onOpenMaterialDetailInNewTab: (warehouseId: string, articleId: string) => void;
  hasPendingRequest: boolean;
}

export function ReceiveNoteCorrectionAddMaterialList(
  props: ReceiveNoteCorrectionAddMaterialListProps
) {
  const hasPendingRequestRef = useLatestValue(props.hasPendingRequest);

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = async ({
    actionKey,
    rowId,
    getColumnData,
  }) => {
    if (hasPendingRequestRef.current) {
      return showNotification.warning(
        i18n.t('entity.warehouse.labels.thereIsAnotherOngoingAction')
      );
    }

    const ids = isArray(rowId) ? rowId : [rowId];

    await match(actionKey)
      .with('addReturn', async () => {
        await props.onAddMaterialToBasket(ids, 'RETURN_TO_SUPPLIER').then(() => {
          refreshDmsDatagrid(WAREHOUSE_GRID_CODES.warehouseCreditNoteItemAdd, {deselectAll: true});
        });
      })
      .with('addValueChange', async () => {
        await props.onAddMaterialToBasket(ids, 'VALUE_CORRECTION').then(() => {
          refreshDmsDatagrid(WAREHOUSE_GRID_CODES.warehouseCreditNoteItemAdd, {deselectAll: true});
        });
      })
      .with('returnAndPriceChange', async () => {
        await props.onAddMaterialToBasket(ids, 'RETURN_AND_PRICE_CHANGE').then(() => {
          refreshDmsDatagrid(WAREHOUSE_GRID_CODES.warehouseCreditNoteItemAdd, {deselectAll: true});
        });
      })
      .with('detail', () => {
        const articleId = getColumnData<string>('articleId')?.value;
        const warehouseId = getColumnData<string>('warehouseId')?.value;

        if (isNil(warehouseId) || isNil(articleId)) {
          return logError('Opening material detail in new tab is not possible');
        }

        props.onOpenMaterialDetailInNewTab(warehouseId, articleId);
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  const queryModifier = useCallback(
    (filter: QueryFilterObject) => mergeAll([filter, {receiveNoteId: props.receiveNoteId}]),
    [props.receiveNoteId]
  );

  return (
    <Card
      title={i18n.t('entity.warehouse.labels.itemsList')}
      isFullHeight
      data-testid={props['data-testid']}
    >
      <DmsDataGrid
        gridCode={WAREHOUSE_GRID_CODES.warehouseCreditNoteItemAdd}
        actionCallback={actionCallback}
        predefinedColumns={PREDEFINED_COLUMNS}
        queryModifier={queryModifier}
        data-testid={suffixTestId('items', props)}
      />
    </Card>
  );
}
